/* eslint-disable max-lines-per-function */
/* istanbul ignore file */
import { Ref, unref, useContext, useRoute, useRouter } from '@nuxtjs/composition-api';
import { isPromotionsRoute } from '@shared/helpers/promotions/isPromotionsRoute';
import { sanitizeLinkUrl } from '@shared/helpers/sanitizeLinkUrl';
import { getPriceRangeQueryValue } from '@shared/helpers/searchFacetFilters/getPriceRangeQueryValue';
import {
  CATEGORY_QUERY_PARAM,
  SEARCH_PRICE_FILTER_KEY,
  SEARCH_PRICE_RANGES,
} from '@shared/system-constants';
import {
  ISearchAvailability,
  ISearchCategoryFilter,
  ISearchPriceRange,
  ISearchPriceRangeFilter,
} from '@shared/types/search';
import { ISBLink } from '@shared/types/storyblok';
import { SortOption } from '@unified-commerce/gpc-vue-storefront-search';
import { Filter } from '@unified-commerce/gpc-vue-storefront-shopify';
import { AgnosticFacet, Logger } from '@vue-storefront/core';
import get from 'lodash/get';
import trim from 'lodash/trim';
import qs from 'qs';
import { Location, Route } from 'vue-router';

import {
  BRAND_CATEGORY_PREFIX_TITLE,
  CATEGORY_ROUTE_PREFIX,
  MADE_TO_ORDER_FILTER,
  MAX_PRICE_RANGE_QUERY_PREFIX,
  MIN_PRICE_RANGE_QUERY_PREFIX,
  PRICE_RANGE_QUERY_PARAM,
  PROMOTION_CATEGORY_PREFIX_TITLE,
  SEARCH_CATEGORY_HANDLE_SEPARATOR,
  SEARCH_CATEGORY_PATH_SEPERATOR,
  SEARCH_CATEGORY_PROMOTIONS_PREFIX_HANDLE,
  SEARCH_CATEGORY_QUERY_SEPARATOR,
  SEARCH_DERIVED_IN_STOCK_FIELD,
  SORT_BY_QUERY_PARAM,
  STORYBLOK_CATEGORY_ROUTE_PREFIX,
} from '~/constants';

import appendAutoplayToYouTubeEmbedURL from './appendAutoplayToYouTubeEmbedURL';
import getBrandLinkFromSlug from './getBrandLinkFromSlug';
import getByString from './getByString';
import getFilterLink from './getFilterLink';
import getPlaceholderImage from './getPlaceholderImage';
import getPromosLinkFromSlug from './getPromosLinkFromSlug';
import getPromotionsCategoryLink from './getPromotionsCategoryLink';
import getSearchQueryLink from './getSearchQueryLink';
import getYouTubeEmbedURL from './getYoutubeEmbedURL';
import isFacetValueInQuery from './isFacetValueInQuery';
import { lockPageScrollAllViewports, lockPageScrollOnMobileOnly } from './lockPageScroll';
import { unlockPageScrollAllViewports, unlockPageScrollOnMobileOnly } from './unlockPageScroll';
export {
  getPlaceholderImage,
  lockPageScrollAllViewports,
  lockPageScrollOnMobileOnly,
  unlockPageScrollAllViewports,
  unlockPageScrollOnMobileOnly,
};

export const getCookieName = (appIdentifier: string, cookieName: string): string =>
  `${appIdentifier}_${cookieName}`;

export const getCTALink = (buttonLink: string | Pick<ISBLink, 'url' | 'cached_url'>) => {
  if (typeof buttonLink === 'string') {
    return sanitizeLinkUrl(buttonLink);
  }
  if (buttonLink?.url.includes('categories') || buttonLink?.cached_url.includes('categories')) {
    return (
      buttonLink?.url.replace('categories/', 'c/') ||
      buttonLink?.cached_url.replace('categories/', 'c/')
    );
  }
  return sanitizeLinkUrl(buttonLink?.url || buttonLink?.cached_url);
};

const nonFilters = ['page', 'sort', 'term', 'itemsPerPage'];

interface IRouteError {
  errorMessage?: string;
  data?: unknown;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const reduceFilters = (query: any) => (prev: any, curr: string) => {
  const makeArray = Array.isArray(query[curr]) || nonFilters.includes(curr);

  return {
    ...prev,
    [curr]: makeArray ? query[curr] : [query[curr]],
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getFiltersDataFromUrl = (query: Record<string, any>, onlyFilters: boolean) => {
  return Object.keys(query)
    .filter((f) => (onlyFilters ? !nonFilters.includes(f) : nonFilters.includes(f)))
    .reduce(reduceFilters(query), {});
};

export const isCategoriesRoute = (routeArg: Ref<Route> | Route) => {
  const route = unref(routeArg);
  return (
    route.path.startsWith(`/${CATEGORY_ROUTE_PREFIX}`) ||
    route.path.startsWith(`/${STORYBLOK_CATEGORY_ROUTE_PREFIX}`)
  );
};

export const getCategoryFromVueRouteQuery = (routeArg: Ref<Route> | Route) => {
  const route = unref(routeArg);
  const categoryQueryParam = route.query[CATEGORY_QUERY_PARAM] ?? '';
  return decodeURIComponent(categoryQueryParam.toString())
    .split(SEARCH_CATEGORY_QUERY_SEPARATOR)
    .join(SEARCH_CATEGORY_HANDLE_SEPARATOR);
};

export const getCategoryFromVueRouteParams = (routeArg: Ref<Route> | Route) => {
  const route = unref(routeArg);
  const categoryParams = Object.values(route.params).filter((param) => !!param);
  return categoryParams.join(SEARCH_CATEGORY_HANDLE_SEPARATOR);
};

export const getActiveCategory = (route: Ref<Route> | Route): string => {
  if (isPromotionsRoute(route)) {
    return `${SEARCH_CATEGORY_PROMOTIONS_PREFIX_HANDLE}${getCategoryFromVueRouteParams(route)}`;
  }
  if (isCategoriesRoute(route)) {
    return getCategoryFromVueRouteParams(route);
  }
  return getCategoryFromVueRouteQuery(route);
};

export const getActiveCategoryTitle = (
  categoryFilters: ISearchCategoryFilter[],
  categoryHandle: string,
): string =>
  categoryFilters.find((categoryFilter) => categoryFilter.exactHandle === categoryHandle)
    ?.exactTitle || '';

export const getPromotionCategoryExactTitle = (categoryTitle: string): string => {
  if (categoryTitle.includes(PROMOTION_CATEGORY_PREFIX_TITLE)) {
    return categoryTitle.split(SEARCH_CATEGORY_PATH_SEPERATOR).pop() || '';
  }
  return '';
};

export const getBrandCategoryExactTitle = (categoryTitle: string): string => {
  if (categoryTitle.includes(BRAND_CATEGORY_PREFIX_TITLE)) {
    return categoryTitle.split(SEARCH_CATEGORY_PATH_SEPERATOR).pop() || '';
  }
  return '';
};

export const getAppliedPriceRanges = (priceRangesQueryParam?: string): ISearchPriceRange[] => {
  if (!priceRangesQueryParam) return [];
  const parsePrice = (priceString: string): number => parseInt(priceString.replace('$', ''), 10);
  const priceRanges = priceRangesQueryParam.split(',');
  return priceRanges.map((priceRange) => {
    if (priceRange.startsWith(MAX_PRICE_RANGE_QUERY_PREFIX)) {
      const maxPrice = parsePrice(priceRange.replace(MAX_PRICE_RANGE_QUERY_PREFIX, ''));
      return { minPrice: null, maxPrice };
    } else if (priceRange.startsWith(MIN_PRICE_RANGE_QUERY_PREFIX)) {
      const minPrice = parsePrice(priceRange.replace(MIN_PRICE_RANGE_QUERY_PREFIX, ''));
      return { minPrice, maxPrice: null };
    }
    const [minPrice, maxPrice] = priceRange.split('-');
    return { minPrice: parsePrice(minPrice), maxPrice: parsePrice(maxPrice) };
  });
};

export const getSearchPriceRangeFilterValue = (priceRanges: ISearchPriceRange[]): string => {
  return priceRanges
    .map((priceRange) => {
      if (!priceRange.minPrice) {
        return `(${SEARCH_PRICE_FILTER_KEY} <= ${priceRange.maxPrice})`;
      }
      if (!priceRange.maxPrice) {
        return `(${SEARCH_PRICE_FILTER_KEY} >= ${priceRange.minPrice})`;
      }
      return `(${SEARCH_PRICE_FILTER_KEY} : ${priceRange.minPrice} TO ${priceRange.maxPrice})`;
    })
    .join(' OR ');
};

export const getSearchAvailabilityFilterValue = (availabilities: ISearchAvailability[]): string => {
  return availabilities
    .map((availability) => {
      if (availability === 'in-stock') {
        return `${SEARCH_DERIVED_IN_STOCK_FIELD}:true`;
      } else if (availability === 'out-of-stock') {
        return `${SEARCH_DERIVED_IN_STOCK_FIELD}:false`;
      } else if (availability === 'made-to-order') {
        return `${MADE_TO_ORDER_FILTER}:true`;
      } else {
        return '';
      }
    })
    .join(' OR ');
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useUiHelpers = () => {
  const route = useRoute();
  const router = useRouter();
  const { error: nuxtError } = useContext();

  const getQueryURL = (paramsToRemove: string[], queriesToAdd: Record<string, string> = {}) => {
    const currentQuery = { ...route.value.query };
    const currentPath = route.value.path;
    paramsToRemove.forEach((param) => {
      delete currentQuery[param];
    });

    const queryURL =
      currentPath + qs.stringify({ ...currentQuery, ...queriesToAdd }, { addQueryPrefix: true });
    return queryURL;
  };
  const setCurrentRouteWithError = (
    statusCode: number,
    errorMessage: string,
    data?: unknown,
  ): void => {
    const { fullPath, ...currentRoute } = route.value;

    const errorContext: Record<string, unknown> = {
      statusCode,
      errorMessage,
      route: currentRoute,
    };

    if (data) {
      errorContext.data = data;
    }

    nuxtError({
      statusCode,
      message: errorMessage,
    });
  };

  const setCurrentRouteAsNotFound = (errorParams?: IRouteError): void => {
    const errorMessage = errorParams?.errorMessage || 'This page could not be found';
    Logger.error(errorMessage, errorParams?.data);
    setCurrentRouteWithError(404, errorMessage, errorParams?.data);
  };

  const setCurrentRouteAsInternalError = (errorParams?: IRouteError): void => {
    const errorMessage = errorParams?.errorMessage || 'Something went wrong';

    setCurrentRouteWithError(500, errorMessage, errorParams?.data);
  };

  const decodeQueryValue = (value: string): string => {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  };

  const getRawQueryParam = (queryParamName: string): string | number | null => {
    const { query } = route.value;

    const value = query[queryParamName] ?? null;

    if (Array.isArray(value)) {
      return value[0];
    }

    return value;
  };

  const getQueryParam = (queryParamName: string): string | null => {
    const value = getRawQueryParam(queryParamName);

    if (typeof value === 'number') {
      return String(value);
    }

    if (typeof value === 'string') {
      return decodeQueryValue(value);
    }

    return value;
  };

  // Multi-select params may be written either as repeated keys (?x=A&x=B,
  // read by vue-router as a real array) or, for older links, as a single
  // comma-joined value (?x=A,B). Repeated keys are preferred for new values
  // since an individual value may itself contain a comma, which would be
  // indistinguishable from the ',' separator in the joined format.
  const getQueryParamArray = (queryParamName: string): string[] => {
    const { query } = route.value;
    const value = query[queryParamName] ?? null;

    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === 'string').map(decodeQueryValue);
    }

    if (typeof value !== 'string' || !value) {
      return [];
    }

    return value.split(',').map(decodeQueryValue);
  };

  const getSortQueryParam = (): SortOption => {
    const sortQueryParam = getQueryParam(SORT_BY_QUERY_PARAM);

    if (sortQueryParam) {
      return decodeURIComponent(sortQueryParam.toString()) as SortOption;
    }

    return '';
  };

  const getCurrentRouteParams = (): Record<string, string> | undefined | null => {
    const { params } = route.value;

    if (Object.entries(params).length === 0) {
      return null;
    }

    return params;
  };

  const getCurrentQuery = (): Location['query'] | null => {
    const { query } = route.value;

    if (Object.entries(query).length === 0) {
      return null;
    }

    return query;
  };

  const getPathParam = (pathParamName: string): string => {
    const { params } = route.value;

    return params[pathParamName];
  };

  const addQueryParam = (
    queryParamName: string,
    queryParamValue: string,
    replaceAllCurrentParams = false,
  ) => {
    const { query } = route.value;

    return qs.stringify(
      replaceAllCurrentParams
        ? {
            [queryParamName]: queryParamValue,
          }
        : {
            ...query,
            [queryParamName]: queryParamValue,
          },
      {
        addQueryPrefix: true,
      },
    );
  };

  const setQueryParamInCurrentPath = async (
    queryParamName: string,
    queryParamValue: string,
  ): Promise<void> => {
    await router.push({
      path: route.value.path,
      query: {
        ...route.value.query,
        [queryParamName]: queryParamValue,
      },
    });
  };

  const removeQueryParam = (queryParamName: string) => {
    const { query, path } = route.value;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [queryParamName]: _, ...restOfQueryParams } = query;

    const queryString = qs.stringify(restOfQueryParams, {
      addQueryPrefix: true,
    });

    if (!queryString) {
      return path;
    }

    return queryString;
  };

  const getFacetsFromURL = () => {
    const { query, params } = route.value;
    const categorySlug = Object.keys(params).reduce(
      (prev, curr) => params[curr] || prev,
      params.slug_1,
    );

    return {
      rootCatSlug: params.slug_1,
      categorySlug,
      page: parseInt(query.page as string, 10) || 1,
      sort: query.sort || 'latest',
      filters: getFiltersDataFromUrl(route.value.query, true),
      itemsPerPage: parseInt(query.itemsPerPage as string, 12) || 20,
      term: query.term,
    };
  };

  /**
   * **NOTE**: This function can only be used on the client-side, where the `window` object is defined.
   *
   * It can be used in `methods` or in client-side lifecycle hooks such as `mounted`.
   */
  const getCurrentAbsoluteURL = (): string => {
    if (!route.value) {
      return '';
    }

    const origin = window.location.origin;
    const routePath = route.value.path;

    return `${origin}${trim(routePath)}`;
  };

  const changeSorting = (sort: string) => {
    const { query } = route.value;
    router.push({ query: { ...query, sort } });
  };

  const changeFilters = (filters: Filter[]) => {
    router.push({
      query: {
        ...getFiltersDataFromUrl(route.value.query, false),
        ...filters,
      },
    });
  };

  const changeItemsPerPage = (itemsPerPage: number) => {
    router.push({
      query: {
        ...getFiltersDataFromUrl(route.value.query, false),
        itemsPerPage,
      },
    });
  };

  const changeSearchTerm = (term: string) => {
    router.push({
      query: {
        ...getFiltersDataFromUrl(route.value.query, false),
        term: term || undefined,
      },
    });
  };

  const getSearchPriceRangeFilter = (): ISearchPriceRangeFilter => {
    const appliedPriceRanges = getAppliedPriceRanges(getQueryParam(PRICE_RANGE_QUERY_PARAM) ?? '');
    const priceRanges = SEARCH_PRICE_RANGES;

    const findCustomPriceRange = (
      priceRanges: ISearchPriceRange[],
      appliedPriceRanges: ISearchPriceRange[],
    ) => {
      for (const AppRange of appliedPriceRanges) {
        if (
          !priceRanges.some(
            (range) => range.minPrice === AppRange.minPrice && range.maxPrice === AppRange.maxPrice,
          )
        ) {
          return AppRange;
        }
      }
      return;
    };

    const customRange = findCustomPriceRange(priceRanges, appliedPriceRanges);

    const minPrice = customRange?.minPrice ?? null;
    const maxPrice = customRange?.maxPrice ?? null;

    return {
      priceRanges,
      minPrice,
      maxPrice,
      appliedPriceRanges,
    };
  };

  const setPriceRangeQuery = async (priceRange: ISearchPriceRange) => {
    router.push({
      query: {
        ...route.value.query,
        [PRICE_RANGE_QUERY_PARAM]: getPriceRangeQueryValue(priceRange),
      },
    });
  };

  const getShopifyCategoryInStory = (
    story: Record<string, unknown>,
    componentName: string,
    shopifyCategoryFieldName = 'product_category',
    shopifyCategoryPluginName = 'shopify_category',
  ) => {
    return get(
      story,
      `content[0].${componentName}.content.${shopifyCategoryFieldName}[0].${shopifyCategoryPluginName}.items[0]`,
      null,
    ) as Record<string, string> | null;
  };

  const isFacetColor = (facet: AgnosticFacet): boolean => facet.id === 'color';

  const isFacetCheckbox = (): boolean => false;

  const getFirstName = (fullName: string) => fullName.split(' ')[0];

  return {
    getCategoryFromVueRouteQuery,
    getActiveCategory,
    getSearchPriceRangeFilter,
    getAppliedPriceRanges,
    setPriceRangeQuery,
    getSearchPriceRangeFilterValue,
    getActiveCategoryTitle,
    getPromotionCategoryExactTitle,
    getBrandCategoryExactTitle,
    setCurrentRouteAsNotFound,
    setCurrentRouteAsInternalError,
    getPathParam,
    getQueryParam,
    getQueryParamArray,
    getSortQueryParam,
    getCurrentQuery,
    addQueryParam,
    removeQueryParam,
    getQueryURL,
    getFacetsFromURL,
    changeSorting,
    changeFilters,
    changeItemsPerPage,
    changeSearchTerm,
    isFacetColor,
    isFacetCheckbox,
    getYouTubeEmbedURL,
    appendAutoplayToYouTubeEmbedURL,
    getFilterLink,
    isFacetValueInQuery,
    getBrandLinkFromSlug,
    getCurrentAbsoluteURL,
    getPlaceholderImage,
    getByString,
    getFirstName,
    getPromosLinkFromSlug,
    getPromotionsCategoryLink,
    getCurrentRouteParams,
    setQueryParamInCurrentPath,
    getSearchQueryLink,
    getShopifyCategoryInStory,
    getCookieName,
    lockPageScrollOnMobileOnly,
    lockPageScrollAllViewports,
    unlockPageScrollOnMobileOnly,
    unlockPageScrollAllViewports,
    getCTALink,
    getSearchAvailabilityFilterValue,
  };
};

export default useUiHelpers;
